import { Parser } from 'expr-eval';

const VERB_REGEX = /\b(count_distinct|sum|min|max|ave|dimension)\(([^)]+)\)/g;

export const normalizeFieldName = (field) => {
  const strippedField = field.replace(/[\s%+*\/()_"'-]+/g, '_').replace(/_+/g, '_');
  switch (strippedField) {
    case 'Products':
    case 'products':
      return 'Product_Name';
    case 'Locations':
    case 'locations':
      return 'Location';
    case 'Gm_Percent':
    case 'Gm_':
    case 'GM_%':
      return 'GM__23';
  }
  const proposedField = field
    .split('.')
    .pop()
    .replace(/%/g, 'percent')
    .replace(/[+]/g, 'plus')
    .replace(/[\-]/g, 'minus')
    .replace(/[*]/g, 'times')
    .replace(/[/]/g, 'over')
    .replace(/[()_"'\s]+/g, '_')
    .replace(/^_/, '')
    .replace(/_$/, '')
    .replace(/_%$/g, '_')
    .split('_')
    .map(word => word.toLowerCase())
    .join('_');

  switch (proposedField) {
    case 'Gm_Percent':
      return 'GM__23';
  }
  return proposedField;
};

export const normalizeRowKeys = (row) => {
  const newRow = {};
  Object.keys(row).forEach((key) => {
    newRow[normalizeFieldName(key)] = row[key];
  });
  return newRow;
};

const extractVerbMatches = (str) => Array.from(str.matchAll(VERB_REGEX));

const normalizeVerbField = (fn, arg) => {
  if (fn === 'dimension') {
    return normalizeFieldName(arg);
  }
  return normalizeFieldName(`${fn}_${arg}`);
};

const normalizeFieldStructureVerb = (value) => {
  const singleMatch = value.match(/^(\b(count_distinct|sum|min|max|ave|dimension))\(([^)]+)\)$/);
  if (singleMatch) {
    const [, , fn, arg] = singleMatch;
    return normalizeVerbField(fn, arg);
  }
  return normalizeFieldName(value);
};

const normalizeExpression = (expr) => {
  return expr.replace(/\b(count_distinct|sum|min|max|ave)\(([^)]+)\)/g, (_, fn, field) =>
    normalizeVerbField(fn, field)
  );
};

export const getParsedField = (parsedFields, fieldName) =>
  parsedFields.find((f) => f.field === fieldName);

export const isGroupLevelExpression = (parsedFields, parsedField) => {
  return (
    parsedField.isCalculated &&
    parsedField.baseFields.every(dep =>
      parsedFields.some(f =>
        f.field === dep &&
        f.aggregateFns.length === 1 &&
        ['count_distinct', 'sum', 'ave', 'min', 'max'].includes(f.aggregateFns[0])
      )
    )
  );
};

export const parseFieldStructure = (element) => {
  const fieldStructure = element.field_structure;

  const fieldNameLookup = {};

  fieldStructure.forEach((f) => {
    const raw = f.value;
    fieldNameLookup[raw] = normalizeFieldStructureVerb(raw);
  });

  return fieldStructure.map((f) => {
    const raw = f.value;
    const isSingleFunction = !!raw.match(/^(\b(count_distinct|sum|min|max|ave|dimension))\([^)]+\)$/);
    const containsMath = /[\+\-\*\/]/.test(raw);
    const isSimple = isSingleFunction && !containsMath;
    const isCalculated = !isSimple;

    const verbMatches = extractVerbMatches(raw);
    const aggregateFns = verbMatches.map(([, fn]) => fn);

    const isAggregate = aggregateFns.some(fn =>
      ['sum', 'ave', 'min', 'max', 'count_distinct'].includes(fn)
    );
    const isDimension = aggregateFns.includes('dimension');

    const dependencies = verbMatches.map(([, fn, arg]) =>
      fieldNameLookup[`${fn}(${arg})`] || normalizeVerbField(fn, arg)
    );

    const field = isSimple
      ? fieldNameLookup[raw]
      : normalizeFieldName(raw);

    return {
      label: f.label,
      field,
      rawExpression: raw,
      normalizedExpression: isCalculated ? normalizeExpression(raw) : null,
      format: f.value_format_name,
      group: f.group || null,
      isCalculated,
      isAggregate,
      isDimension,
      aggregateFns,
      baseFields: verbMatches.map(([, , arg]) => arg),
      dependencies,
    };
  });
};

export const getAllFieldsByVerb = (parsedFields, verb) =>
  parsedFields.filter(f =>
    f.aggregateFns.includes(verb)
  ).map(f => f.field);

export const getFieldByVerb = (parsedFields, verb) => {
  const match = parsedFields.find(f =>
    f.aggregateFns.includes(verb)
  );
  return match?.field;
};

export const denormalizeFieldName = (field) => {
  switch (field) {
    case 'On_Hand':
      return 'Units On Hand';
    case 'Gm_Percent':
      return 'GM %';
    case 'Wos':
      return 'WOS';
  }
  return field.replace(/[_]/g, ' ').toLowerCase();
}

export const findChildItems = (category, target) => {
  const { children, name } = category;
  if (name === target) return [category];
  if (!children || !children.length) return;
  return children.reduce((acc, child) => {
    const result = findChildItems(child, target);
    if (result) return [...acc, ...result];
    return acc;
  }, []);
};

export const buildHierarchy = (data) => {
  const categories = new Map();
  const PARENT_ID = 1;

  data.forEach((item) => {
    const { Category: category, Sub_Category: subCategory } = item;

    if (!categories.has(category)) {
      categories.set(category, {
        id: category,
        name: category,
        parent_id: PARENT_ID,
        children: [],
      });
    }

    if (subCategory) {
      const categoryNode = categories.get(category);
      if (!categoryNode.children.some((child) => child.name === subCategory))
        categoryNode.children.push({
          id: `${category}-${subCategory}`,
          name: subCategory,
          parent_id: category,
          children: [],
        });
    }
  });

  return [{
    id: PARENT_ID,
    name: 'Products',
    parent_id: null,
    children: Array.from(categories.values()).map((category) => ({
      ...category,
      children: category.children.sort((a, b) => a.name.localeCompare(b.name)),
    })).sort((a, b) => a.name.localeCompare(b.name)),
  }];
};

export const formatValue = (val, format) => {
  if (val == null) return '—';

  switch (format) {
    case 'usd_0':
      return val.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    case 'decimal_0':
      return Number(val).toFixed(0);
    case 'decimal_1':
      return Number(val).toFixed(1);
    case 'percent_1':
      return `${(val * 100).toFixed(1)}%`;
    default:
      return typeof val === 'number' ? val.toFixed(1) : val;
  }
};

export const getMinWidthForField = (field, label) => {
  if (label === 'Cost On Hand') {
    return '150';
  }
  switch (field) {
    case 'Gm_Percent':
    case 'Region':
    case 'Wos':
      return 75;
    case 'Ave_Weekly_Unit_Sales':
    case 'Cost_On_Hand':
    case 'On_Hand':
    case 'Product_Name':
    case 'Location':
      return 150;
    default:
      return 100;
  }
};

export const groupAndCalculate = ({
  data,
  parsedFields,
  groupKeys = [],
  pivotKey = null,
}) => {
  const parser = new Parser();

  const aggregates = parsedFields.filter(f => f.isAggregate && !f.isCalculated);
  const calculated = parsedFields.filter(f => f.isCalculated);

  const groupedMap = new Map();
  data.forEach((row, i) => {
    const dimKey = groupKeys.map(k => row[k] ?? 'Unknown').join('||');
    const pivotValue = pivotKey ? row[pivotKey] ?? 'Default' : 'Default';

    if (!groupedMap.has(dimKey)) {
      groupedMap.set(dimKey, {
        __agg__: {},
        ...Object.fromEntries(groupKeys.map(k => [k, row[k] ?? 'Unknown'])),
      });
    }

    const entry = groupedMap.get(dimKey);

    aggregates.forEach((aggField) => {
      const { aggregateFns, baseFields } = aggField;

      baseFields.forEach((baseField) => {
        const val = row[baseField] ?? 0;

        aggregateFns.forEach((fn) => {
          const fieldKey = normalizeFieldName(`${fn}_${baseField}`);
          const pivotMap = entry.__agg__[pivotValue] ?? {};
          const prev = pivotMap[fieldKey];

          switch (fn) {
            case 'sum':
              pivotMap[fieldKey] = (prev || 0) + val;
              break;
            case 'min':
              pivotMap[fieldKey] = prev == null ? val : Math.min(prev, val);
              break;
            case 'max':
              pivotMap[fieldKey] = prev == null ? val : Math.max(prev, val);
              break;
            case 'ave': {
              const prevObj = prev || { sum: 0, count: 0 };
              pivotMap[fieldKey] = {
                sum: prevObj.sum + val,
                count: prevObj.count + 1,
              };
              break;
            }
            case 'count_distinct': {
              const set = prev || new Set();
              set.add(val);
              pivotMap[fieldKey] = set;
              break;
            }
          }

          entry.__agg__[pivotValue] = pivotMap;
        });
      });
    });
  });

  const resultRows = Array.from(groupedMap.entries()).map(([_, entry], index) => {
    const { __agg__, ...dimValues } = entry;
    const row = {
      id: index,
      ...dimValues,
    };

    Object.entries(__agg__).forEach(([pivotVal, metrics]) => {
      Object.entries(metrics).forEach(([key, val]) => {
        const finalKey = pivotKey ? `${pivotVal}__${key}` : key;
        if (val instanceof Set) {
          row[finalKey] = val.size;
        } else if (typeof val === 'object' && val.sum != null) {
          row[finalKey] = val.count ? val.sum / val.count : 0;
        } else {
          row[finalKey] = val;
        }
      });

      // Evaluate calculated fields
      const available = new Set(Object.keys(row));
      const alreadyDone = new Set();
      let remaining = [...calculated];
      let loopCount = 0;

      while (remaining.length && loopCount++ < 50) {
        const nextRound = [];

        remaining.forEach(f => {
          if (alreadyDone.has(f.field)) return;

          const canEval = f.dependencies.every(dep => available.has(dep));

          if (canEval) {
            try {
              const expr = parser.parse(f.normalizedExpression);
              row[f.field] = expr.evaluate(row);
              available.add(f.field);
              alreadyDone.add(f.field);
            } catch (e) {
              // console.warn('Defaulting to `0` for unresolved expression:', f.field);
              row[f.field] = 0;
              // console.warn(`Failed to evaluate ${f.field}`, e);
            }
          } else {
            nextRound.push(f);
          }
        });

        if (nextRound.length === remaining.length) {
          // console.warn('Unresolvable expressions:', nextRound.map(f => f.field));
          nextRound.forEach(f => {
            // console.warn('Defaulting to `0` for unresolvable expression:', f.field);
            row[f.field] = 0; // fallback for unresolvable expressions
          });
          break;
        }

        remaining = nextRound;
      }
    });

    return row;
  });

  return resultRows;
};
